import { createUseStyles } from 'react-jss';
import { CmsWrenchTheme } from '~app/styles/cmsTheme';

type StyleProps = {
  utSource2?: string;
};

const useStyle = createUseStyles<string, StyleProps, CmsWrenchTheme>((theme: CmsWrenchTheme) => {
  const { breakpoints, brandColors, type } = theme;

  const mediaQueries = Object.fromEntries(
    Object.entries(breakpoints).map(([key, value]) => [key, `@media (min-width: ${value}px)`])
  ) as Record<keyof typeof breakpoints, string>;

  const utilities = {
    textCenter: { textAlign: 'center' },
    textLeft: { textAlign: 'left' },
    fullWidth: { width: '100%' },
  };

  const containerBase = {
    padding: '20px',
    maxWidth: '1400px',
    margin: '0 auto',
    overflowX: 'hidden',
    alignItems: 'center',
    [mediaQueries.sm]: { padding: '40px 60px 100px' },
    [mediaQueries.lg]: { padding: '80px 0 100px 100px', alignItems: 'flex-start' },
    [mediaQueries.xl]: { padding: '80px 0 100px 165px' },
  };

  const createTextStyle = (maxWidth: string, lgMaxWidth?: string): object => ({
    fontWeight: type.fontWeight.regular,
    ...utilities.fullWidth,
    maxWidth,
    '&, & div': utilities.textCenter,
    [mediaQueries.lg]: {
      maxWidth: lgMaxWidth ?? maxWidth,
      '&, & div': utilities.textLeft,
    },
  });

  return {
    display: { backgroundColor: brandColors.primary.vine },
    wrapper: containerBase,
    wrapperV1: { ...containerBase, [mediaQueries.sm]: { padding: '40px 60px 100px' } },

    contentContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '40px',
      [`@media (min-width: ${breakpoints.md + 1}px)`]: {
        flexDirection: 'row',
        justifyContent: 'space-between',
      },
      [mediaQueries.lg]: { alignItems: 'flex-start' },
    },

    ctaComponent: { margin: '0 auto', maxWidth: 380 },
    ctaComponentV1: {
      margin: '0 auto',
      maxWidth: 547,
      borderRadius: [[8, 8, 0, 0]],
      padding: '24px',
      backgroundColor: '#fff',
      [mediaQueries.sm]: { borderRadius: 8, padding: [[48, 64]] },
    },
    imageBase: {
      objectFit: 'contain',
      objectPosition: 'center',
    },

    imageV1: {
      [mediaQueries.md]: { width: 550, height: 460 },
      [mediaQueries.xl]: { width: 620, height: 504, margin: '200px 0 0 150px' },
    },

    imageV2: {
      width: 300,
      height: 285,
      [mediaQueries.md]: { width: 430, height: 385 },
      [mediaQueries.lg]: { width: 430, height: 385, marginLeft: 60 },
      [mediaQueries.xl]: { width: 490, height: 385, marginLeft: 200 },
    },

    imageResponsive: ({ utSource2 }: StyleProps) => ({
      width: 350,
      height: 300,
      [mediaQueries.md]: { width: 500, height: 460 },
      [mediaQueries.xl]: {
        width: 530,
        height: 460,
        marginLeft: 150,
        marginTop: utSource2 === 'RebrandEpForm' ? 60 : 0,
      },
    }),

    header: {
      '& > section': {
        ...utilities.textCenter,
        ...utilities.fullWidth,
        maxWidth: 1020,
        [mediaQueries.md]: { fontSize: 34 },
        [mediaQueries.lg]: { ...utilities.textLeft, fontSize: 50 },
      },
      [mediaQueries.lg]: utilities.fullWidth,
    },
    subHeader: createTextStyle('1020px', '560px'),
    intlSubheader: createTextStyle('1020px', '460px'),
  };
});

export default useStyle;
